import { cn } from '@invent/wl-ui-kit-next';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@invent/wl-ui-kit-next/ui';
import { ChevronDown, ChevronsUpDown, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import * as React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { useMockOpens } from '@/demo';
import { Lockup } from '@/design';
import { TEAM_PATHS } from '@/domains/admin';
import {
  initialsFromEmail,
  roleNames,
  useAuth,
  useCan,
  type IRoleRef
} from '@/domains/auth';
import { TEAM_HOME, entryTarget, teamNavFor, type ITeamNavEntry } from './nav';

/**
 * The Team Portal's rail: Midnight Blue, 248 px — room for the longest entries, "Compliance queues"
 * and "Metrics updates", and for an entry's "coming" tag — collapsible to its icons and remembered per viewer (the two-portals
 * design, §4.2).
 *
 * The current entry sits on a Royal Blue block with a Turquoise bar; a 2 px Teal → Turquoise line
 * runs down the rail's edge — the second of the guidelines' approved gradients, and this site's
 * signature the way Royal Blue → Sapphire is the Advisor Portal's. Slight Blue on Midnight is
 * 13.8:1; the group labels, Ocean Blue, 7.5:1.
 *
 * Each group's heading is a button that folds its entries, kept per browser (`useRailFolds`, held
 * by the shell); the group holding the current screen stays open and says why. The icon rail has
 * no headings and draws every entry.
 *
 * The landmark is named "Team Portal", and it is the only navigation on a Team Portal screen that
 * leaves the screen: the builder's and the register's tabs are "Section", the hub's section nav
 * "On this page".
 *
 * With the demo on, an entry with no screen links to the demo's mock of it, tagged "mock" as the
 * live ones are not, and is left out for a role the demo is viewing as that the role matrix does
 * not grant it (`useMockOpens`). With the demo off, every such entry is "coming".
 *
 * A live entry carries the same "mock" tag when its section still draws invented data on some of
 * its screens (`invented` in `nav.ts`) — with the demo off too, since those screens are there
 * either way.
 */
export function TeamRail({
  collapsed,
  onToggle,
  folds,
  onSignOut,
  inSheet = false
}: {
  collapsed: boolean;
  onToggle: () => void;
  /** The groups folded, held by the shell: the desktop rail and the sheet's copy are both mounted
   *  and must not each keep a list of their own. */
  folds: IRailFolds;
  onSignOut: () => void;
  /** Drawn inside the narrow screen's sheet: full height of it, never collapsed, no toggle. */
  inSheet?: boolean;
}) {
  const { user } = useAuth();
  const can = useCan();
  const opens = useMockOpens();
  const { pathname } = useLocation();
  const groups = teamNavFor(can)
    .map((group) => ({
      ...group,
      entries: group.entries.filter((entry) => {
        const target = entryTarget(entry);
        return !target?.mock || !entry.mock || opens(entry.mock.screen);
      })
    }))
    .filter((group) => group.entries.length > 0);

  return (
    <aside
      className={cn(
        'z-30 flex shrink-0 flex-col bg-[var(--color-midnight-blue)] text-[var(--color-slight-blue)] transition-[width] duration-150',
        inSheet ? 'h-full w-full' : 'sticky top-0 hidden h-screen lg:flex',
        !inSheet && (collapsed ? 'w-14' : 'w-62')
      )}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 w-0.5 bg-linear-to-b from-[var(--color-teal)] to-[var(--color-turquoise)]"
      />

      <div className={cn('flex h-12 shrink-0 items-center', collapsed ? 'justify-center' : 'px-4')}>
        <Link
          to={TEAM_PATHS.home}
          className="rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-turquoise)]"
        >
          <Lockup zoneName="Team Portal" tone="dark" compact={collapsed} />
        </Link>
      </div>

      <nav aria-label="Team Portal" className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        <ul>
          <li>
            <RailEntry entry={TEAM_HOME} collapsed={collapsed} />
          </li>
        </ul>
        {groups.map((group) => {
          const pinned = group.entries.some((entry) => holds(entry, pathname));
          return (
            <RailGroup
              key={group.label}
              label={group.label}
              collapsed={collapsed}
              folded={folds.closed.includes(group.label) && !pinned}
              pinned={pinned}
              onToggle={() => folds.toggle(group.label)}
            >
              {group.entries.map((entry) => (
                <li key={entry.label}>
                  <RailEntry entry={entry} collapsed={collapsed} />
                </li>
              ))}
            </RailGroup>
          );
        })}
      </nav>

      <div className="shrink-0 border-t border-white/10 p-2">
        {!inSheet && (
          <button
            type="button"
            onClick={onToggle}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            className={cn(
              'mb-1 flex h-7 w-full items-center gap-2 rounded-md px-2.5 text-xs text-[var(--color-ocean-blue)] outline-none hover:bg-white/5 hover:text-white focus-visible:ring-2 focus-visible:ring-[var(--color-turquoise)]',
              collapsed && 'justify-center px-0'
            )}
          >
            {collapsed ? (
              <PanelLeftOpen className="size-4" aria-hidden />
            ) : (
              <>
                <PanelLeftClose className="size-4" aria-hidden />
                Collapse
              </>
            )}
          </button>
        )}
        {user && (
          <RailAccount
            email={user.email}
            roles={user.roles}
            collapsed={collapsed}
            onSignOut={onSignOut}
          />
        )}
      </div>
    </aside>
  );
}

const CLOSED_GROUPS_KEY = 'veritant.team.rail-groups-closed';

export interface IRailFolds {
  closed: readonly string[];
  toggle: (label: string) => void;
}

/** The groups folded, kept on this browser (SR-438). Called once, by the shell. */
export function useRailFolds(): IRailFolds {
  const [closed, setClosed] = React.useState(readClosedGroups);
  const toggle = React.useCallback(
    (label: string) => {
      const next = closed.includes(label) ? closed.filter((l) => l !== label) : [...closed, label];
      setClosed(next);
      writeClosedGroups(next);
    },
    [closed]
  );
  return { closed, toggle };
}

/** The groups the viewer folded, by label, on this browser. An unreadable or refused store leaves
 *  every group open — the rail works the same, only forgetting. */
function readClosedGroups(): string[] {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(CLOSED_GROUPS_KEY) ?? '[]') as unknown;
    return Array.isArray(parsed) ? parsed.filter((l): l is string => typeof l === 'string') : [];
  } catch {
    return [];
  }
}

function writeClosedGroups(labels: string[]): void {
  try {
    window.localStorage.setItem(CLOSED_GROUPS_KEY, JSON.stringify(labels));
  } catch {
    // Kept for this page load only, in the rail's state.
  }
}

/** Whether `pathname` is on the entry's screen or one of its tabs — the way `NavLink` lights it. */
function holds(entry: ITeamNavEntry, pathname: string): boolean {
  const paths = [entryTarget(entry)?.to, ...(entry.lit ?? [])].filter((p): p is string => !!p);
  return paths.some((path) => {
    const bare = path.split(/[?#]/)[0];
    return pathname === bare || pathname.startsWith(`${bare}/`);
  });
}

function RailGroup({
  label,
  collapsed,
  folded,
  pinned,
  onToggle,
  children
}: {
  label: string;
  collapsed: boolean;
  /** Its entries are hidden — never while the rail is icons only, which draws no headings. */
  folded: boolean;
  /** It holds the current screen: it stays open, and its heading says it cannot be folded. */
  pinned: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const id = React.useId();
  const listId = `${id}-list`;
  const hidden = folded && !collapsed;
  return (
    <div className="mt-3">
      {collapsed ? (
        <div className="mx-2 mb-1 border-t border-white/10">
          <span id={id} className="sr-only">
            {label}
          </span>
        </div>
      ) : (
        <button
          type="button"
          id={id}
          aria-expanded={!hidden}
          aria-controls={listId}
          aria-disabled={pinned || undefined}
          aria-describedby={pinned ? `${id}-why` : undefined}
          onClick={pinned ? undefined : onToggle}
          className={cn(
            'mb-0.5 flex w-full items-center justify-between rounded-sm px-2.5 text-left text-[0.71875rem] font-medium text-[var(--color-ocean-blue)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-turquoise)]',
            pinned ? 'cursor-default' : 'hover:text-white'
          )}
        >
          {label}
          <ChevronDown
            className={cn('size-3.5 shrink-0 transition-transform', hidden && '-rotate-90')}
            aria-hidden
          />
        </button>
      )}
      {pinned && !collapsed && (
        <span id={`${id}-why`} className="sr-only">
          Holds the current screen, so it stays open
        </span>
      )}
      <ul id={listId} aria-labelledby={id} hidden={hidden}>
        {children}
      </ul>
    </div>
  );
}

const ENTRY =
  'relative flex h-7 items-center gap-2.5 rounded-md px-2.5 text-[0.8125rem] outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-turquoise)]';

function RailEntry({ entry, collapsed }: { entry: ITeamNavEntry; collapsed: boolean }) {
  const { pathname } = useLocation();
  // A tabbed area is lit on every tab it holds, not on its first alone ("Users & roles"): on one,
  // the entry leads to that tab, so `NavLink` marks it current as it marks any other.
  const onTab = (entry.lit ?? []).find(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );
  const Icon = entry.icon;
  const target = entryTarget(entry);
  // The demo's stand-in for a section with no screen, or a live section some of whose screens
  // still draw fixtures. One tag for both: what it tells the reader is the same thing.
  const invented = !!target?.mock || !!entry.invented;

  if (!target) {
    // No screen yet: not a link, and said so — to a screen reader too, since "coming" is text.
    return (
      <span
        aria-disabled="true"
        title={collapsed ? `${entry.label} (coming)` : undefined}
        className={cn(
          ENTRY,
          'cursor-default text-[color-mix(in_srgb,var(--color-slight-blue)_52%,var(--color-midnight-blue))]',
          collapsed && 'justify-center px-0'
        )}
      >
        <Icon className="size-4 shrink-0" aria-hidden />
        <span className={cn('truncate', collapsed && 'sr-only')}>{entry.label}</span>
        <span
          className={cn(
            'ml-auto rounded-full border border-dashed border-current px-1.5 text-[0.625rem] leading-[0.875rem]',
            collapsed && 'sr-only'
          )}
        >
          coming
        </span>
      </span>
    );
  }

  return (
    <NavLink
      to={onTab ?? target.to}
      end={entry.end}
      title={collapsed ? `${entry.label}${invented ? ' (mock)' : ''}` : undefined}
      className={({ isActive }) =>
        cn(
          ENTRY,
          collapsed && 'justify-center px-0',
          isActive
            ? 'bg-[var(--color-royal-blue)] font-medium text-white before:absolute before:inset-y-1.5 before:left-0 before:w-[3px] before:rounded-full before:bg-[var(--color-turquoise)]'
            : 'text-[var(--color-slight-blue)] hover:bg-white/5 hover:text-white'
        )
      }
    >
      <Icon className="size-4 shrink-0" aria-hidden />
      <span className={cn('truncate', collapsed && 'sr-only')}>{entry.label}</span>
      {invented && (
        // Said as "coming" is, to a screen reader too: Yellow on Midnight, the mock tone, 12:1.
        <span
          className={cn(
            'ml-auto rounded-full border border-dashed border-[var(--color-yellow)] px-1.5 text-[0.625rem] leading-[0.875rem] text-[var(--color-yellow)]',
            collapsed && 'sr-only'
          )}
        >
          mock
        </span>
      )}
    </NavLink>
  );
}

/** Who is signed in, and the one thing an account does from anywhere: sign out. Named "Account
 *  menu" in both portals, as it always was. */
function RailAccount({
  email,
  roles,
  collapsed,
  onSignOut
}: {
  email: string;
  roles: readonly IRoleRef[];
  collapsed: boolean;
  onSignOut: () => void;
}) {
  // The roles by name, as survey-api names them — never a word for the kind, which every account
  // here shares. A Team Portal account given none yet is said so (the role-model UI design, §5).
  const held = roleNames(roles) ?? 'No Team Portal role yet';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          aria-label="Account menu"
          className={cn(
            'h-auto w-full justify-start gap-2.5 rounded-md px-2 py-1.5 text-left text-[var(--color-slight-blue)] hover:bg-white/5 hover:text-white',
            collapsed && 'justify-center px-0'
          )}
        >
          <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[var(--color-teal)] text-[0.6875rem] font-medium text-white">
            {initialsFromEmail(email)}
          </span>
          {!collapsed && (
            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs font-medium text-white">{email}</span>
              <span className="block truncate text-[0.6875rem] text-[var(--color-ocean-blue)]">
                {held}
              </span>
            </span>
          )}
          {!collapsed && <ChevronsUpDown className="size-3.5 shrink-0 opacity-70" aria-hidden />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="right" align="end" className="w-64">
        <DropdownMenuLabel className="grid gap-0.5">
          <span className="text-sm font-medium break-all">{email}</span>
          <span className="text-xs font-normal text-muted-foreground">{held}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onSignOut}>Log out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
